import * as react from 'react'
import { useEffect, useState } from 'react'

import { InteractionType } from '@azure/msal-browser'
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsalAuthentication,
} from '@azure/msal-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'

import { loginRequest } from './AuthConfig'
import { useNetwork } from './context/providers/NetworkContext'
import { createSnackBar, errorSnackBar } from './services/SnackBarService'
import { ConfigProvider } from '@/context/providers/ConfigProvider'
import { DeviceProvider } from '@/context/providers/DeviceProvider'
import { getEnv } from '@/hooks/useConfig'
import { useIPadKeyboardHandler } from '@/hooks/useIPadKeyboardHandler'
import './App.scss'
import NetworkInterceptor from '@/services/NetworkInterceptor'

declare global {
  namespace React.JSX {
    interface IntrinsicElements {
      'system-version': { system: string; env: 'dev' | 'prod' }
    }
  }
}

const CanvasWrapper = react.lazy(() => import('@/pages/CanvasWrapper/CanvasWrapper'))
const Home = react.lazy(() => import('@/pages/Home/Home'))

const AppContent = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
  useIPadKeyboardHandler()
  return <>{children}</>
}

const ReleaseNotes = (): React.JSX.Element | null => {
  const environment = getEnv()

  if (!environment) return null

  return <system-version system="Assortment Canvas" env={environment === 'prod' ? 'prod' : 'dev'} />
}

const App = (): React.JSX.Element => {
  const { isOnline } = useNetwork()

  useMsalAuthentication(InteractionType.Redirect, loginRequest)

  useEffect(() => {
    if (!isOnline) {
      errorSnackBar(
        'Network connection lost',
        'Changes will not be saved until the connection is restored.',
      )
    } else createSnackBar(false)
  }, [isOnline])

  const [queryClient] = useState(() => new QueryClient())
  return (
    <DeviceProvider>
      <AppContent>
        <div className="app-container">
          <AuthenticatedTemplate>
            <ConfigProvider>
              <NetworkInterceptor>
                <QueryClientProvider client={queryClient}>
                  <BrowserRouter>
                    <Routes>
                      <Route index element={<Navigate to="/canvas/home" replace />} />
                      <Route path="/canvas">
                        <Route index element={<Navigate to="/home" replace />} />
                        <Route
                          path="home"
                          element={
                            <react.Suspense fallback={<div>Loading Home...</div>}>
                              <Home />
                            </react.Suspense>
                          }
                        />
                        <Route
                          path=":seasonId/:divisionId/:sectionId/:deptId/:canvasId/:isPresentReadMode?"
                          element={
                            <react.Suspense fallback={<div>Loading CanvasWrapper...</div>}>
                              <CanvasWrapper />
                            </react.Suspense>
                          }
                        />
                      </Route>
                      <Route path="*" element={<Navigate to="/canvas/home" replace />} />
                    </Routes>
                  </BrowserRouter>
                  {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
                </QueryClientProvider>
              </NetworkInterceptor>
              <ReleaseNotes />
            </ConfigProvider>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <div>Signing in...</div>
          </UnauthenticatedTemplate>
        </div>
      </AppContent>
    </DeviceProvider>
  )
}

export default App
